import React, { type VFC, useCallback, useEffect, useRef, useState } from 'react';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { updatedUnsubScreensEnabled } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNewUnsub';
import Content from 'reactApp/ui/Content/Content';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { FooterButton } from 'reactApp/ui/PollPopup/FooterButton';
import { PollContent } from 'reactApp/ui/PollPopup/PollContent';
import { MAX_COMMENT_LENGTH, Reasons } from 'reactApp/ui/PollPopup/PollPopup.constants';
import { sendDwh } from 'reactApp/utils/ga';
import { ECategoryGa } from 'reactApp/utils/paymentGa';
import { getTrimmedText } from 'reactApp/utils/textHelpers';

import { Dialog } from '../Dialog/Dialog';
import styles from './PollPopup.css';

export interface PollPopupProps {
    isPhone?: boolean;
    onSubmit?: (choice: string) => void;
    onClose?: () => void;
}

export const PollPopup: VFC<PollPopupProps> = ({ onClose, isPhone = false, onSubmit = onClose }) => {
    const source = isPhone ? 'touch' : 'desktop';
    const [choiceId, setChoiceId] = useState('');
    const [comment, setComment] = useState('');
    const handleOnClose = useCallback(() => onClose?.(), [onClose]);
    const ref = useRef<HTMLDivElement>(null);

    const enableSubmit = Boolean(choiceId === Reasons.OTHER_ID ? comment : choiceId);

    useEffect(() => {
        emitAnalyticEvent(AnalyticEventNames.POLL_OPEN);
        sendDwh({ eventCategory: ECategoryGa.payment, action: 'poll-open', dwhData: { source } });
    }, []);

    const sumbitCallback = useCallback(() => {
        onSubmit?.(choiceId);
    }, [onSubmit, choiceId]);

    const handleSubmit = useCallback(() => {
        if (!enableSubmit) {
            return;
        }

        const result = `${choiceId === Reasons.OTHER_ID ? getTrimmedText(comment, MAX_COMMENT_LENGTH) : choiceId}`;

        emitAnalyticEvent(AnalyticEventNames.POLL_SEND);
        sendDwh({ eventCategory: ECategoryGa.payment, action: 'poll-send', dwhData: { result, source } });
        sumbitCallback();
    }, [choiceId, comment, enableSubmit, sumbitCallback]);

    const footer = <FooterButton onClick={handleSubmit} enableSubmit={enableSubmit} isPhone={isPhone} />;

    const choiceClick = useCallback(
        (id: string) => {
            setChoiceId(id);
            if (id !== Reasons.OTHER_ID) {
                setComment('');
            }
        },
        [setChoiceId, setComment]
    );

    const handleOnComment = useCallback(
        (comment: string) => {
            setComment(comment);
        },
        [setComment]
    );

    const content = (
        <PollContent onComment={handleOnComment} onChoice={choiceClick} isPhone={isPhone} choiceId={choiceId} comment={comment} />
    );

    if (isPhone) {
        return (
            <MobileDialog
                id={Reasons.POLL_DLG_ID}
                open
                dimmer
                onClose={handleOnClose}
                closable
                closeOnDimmerClick
                mod="confirm"
                title={<></>}
                footer={footer}
                topmost
                scrollRef={ref}
                isRebrandingQuotaLanding
            >
                <div ref={ref} className={styles.contentWrapper}>
                    {content}
                </div>
            </MobileDialog>
        );
    }

    return (
        <Dialog
            id={Reasons.POLL_DLG_ID}
            open
            dimmer
            className={updatedUnsubScreensEnabled ? styles.rootBig : styles.root}
            header={<></>}
            onCancel={handleOnClose}
            footer={footer}
            closable
        >
            <Content isModal>
                <div className={styles.contentWrapper}>{content}</div>
            </Content>
        </Dialog>
    );
};

PollPopup.displayName = 'PollPopup';
